import * as React from 'react';
import { Pressable, View } from 'react-native';

import { Text } from '@/components/ui/text';
import { useLocationPermission } from '@/features/onboarding/hooks/use-location-permission';
import { SetupLayout } from '@/features/setup/components/setup-layout';
import { useSetupContext } from '@/features/setup/setup-provider';
import { PermissionsNotificationsSection } from './permissions-notifications';

const STEP = 2;

export function PermissionsScreen() {
  const { advance, goBack, isAdvancing } = useSetupContext();
  const { status: locationStatus, requestPermission } = useLocationPermission();

  return (
    <SetupLayout
      step={STEP}
      onBack={goBack}
      primaryLabel="Continue"
      onPrimary={advance}
      primaryLoading={isAdvancing}
    >
      <Text className="mb-2 text-xl font-bold text-foreground">Permissions</Text>
      <Text className="mb-6 text-sm text-muted-foreground">
        We need a couple of permissions to give you the best experience.
      </Text>

      <View className="mb-6">
        <Text className="mb-1 text-base font-semibold text-foreground">Location</Text>
        <Text className="mb-3 text-sm text-muted-foreground">
          Used for finding nearby restaurants, check-ins, and recommendations.
        </Text>

        {/* The shared provider does not acquire until setup finishes (ADR 0006),
            so there is no stale refusal to heal here -- it picks the grant up on
            its first acquisition, once the app opens. */}
        <LocationAction status={locationStatus} onAllow={requestPermission} />
      </View>

      <PermissionsNotificationsSection />
    </SetupLayout>
  );
}

function LocationAction({
  status,
  onAllow,
}: {
  status: ReturnType<typeof useLocationPermission>['status'];
  onAllow: () => void;
}) {
  if (status === 'granted') {
    return (
      <View className="rounded-lg bg-green-100 px-4 py-2">
        <Text className="text-sm text-green-800">Location access granted</Text>
      </View>
    );
  }

  if (status === 'denied') {
    return (
      <View>
        <Text className="mb-2 text-sm text-muted-foreground">
          Location helps us show restaurants near you. You can enable it later in Settings.
        </Text>
        <Pressable
          onPress={onAllow}
          accessibilityRole="button"
          accessibilityLabel="Allow Location"
          className="rounded-lg bg-surface-secondary px-4 py-2"
        >
          <Text className="text-center text-sm text-foreground">Try Again</Text>
        </Pressable>
      </View>
    );
  }

  return (
    <Pressable
      onPress={onAllow}
      accessibilityRole="button"
      accessibilityLabel="Allow Location"
      className="rounded-lg bg-primary px-4 py-2"
    >
      <Text className="text-center text-sm text-primary-foreground">Allow Location</Text>
    </Pressable>
  );
}
